/**
 * 投稿の日本語翻訳 (ブラウザ内 LLM 使用)。
 *
 * - `shouldAutoTranslate(text)`: 自動翻訳の対象か (日本語比率が低く、かつ空でない)
 * - `translateToJapanese(uri, text)`: キャッシュ → 直列キュー → LLM → キャッシュ保存
 * - `useTranslation(uri, text)`: React フック。設定が ON なら自動で翻訳開始、
 *   OFF なら `triggerTranslate()` 手動呼び出しで開始。
 *
 * Backend は local-llm.ts の抽象に委譲 (Gemini Nano、Nano の無い端末は導入済みなら sarashina)。
 * sarashina だけはプロンプトと後処理が専用 (sarashina-translate.ts)。LLM 利用不可な環境では翻訳 OFF (caller 側で UI 非表示)。
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { generateWithLocalLLM, pickLocalLLM, resetLocalLLMChoice, type LocalLLM } from './local-llm';
import { loadSarashina, SARASHINA, SarashinaNotInstalledError } from './sarashina-llm';
import {
  cleanSarashinaParagraph,
  sarashinaMaxNewTokens,
  sarashinaTranslateInput,
  splitParagraphs,
} from './sarashina-translate';
import { hasJapanese, preprocessText } from './japanese-text';
import { loadCachedTranslation, saveCachedTranslation } from './translation-idb';
import { getAutoTranslate } from './prefs';
import { stripMarkdown, stripWrappers } from './flavor-text';

const MIN_TEXT_LEN = 10; // これ未満は翻訳しない (挨拶・絵文字のみなど)
const TIMEOUT_MS = 60_000;

/**
 * 翻訳対象かどうかを判定。
 * 1) post.record.langs が明示されていればそれを最優先で信頼する
 *    (投稿者本人 or クライアントがタグ付けしたもの)。'ja' が含まれていれば対象外。
 * 2) langs が空 / 未指定のときのみ、本文のヒューリスティック (日本語比率) で判定。
 */
export function shouldAutoTranslate(text: string, langs?: string[] | undefined): boolean {
  const stripped = preprocessText(text).trim();
  if (stripped.length < MIN_TEXT_LEN) return false;
  if (langs && langs.length > 0) {
    const norm = langs.map((l) => l.toLowerCase().split(/[-_]/)[0] ?? '');
    if (norm.includes('ja')) return false;
    return true;
  }
  return !hasJapanese(stripped, 0.2);
}

// ── 直列キュー ──
// ローカル LLM は 1 本しか捌けないので、複数の翻訳要求は並列に投げず
// 順番に処理する。Promise チェーンで素朴に直列化。
let chain: Promise<unknown> = Promise.resolve();
function enqueue<T>(fn: () => Promise<T>): Promise<T> {
  const run = chain.then(
    () => fn(),
    () => fn(),
  );
  chain = run;
  return run;
}

/**
 * 元テキストから翻訳対象の本文と、末尾に付け戻す要素 (URL / ハッシュタグ / メンション) を分離する。
 * LLM には本文だけを渡し、訳文の末尾にそのまま連結する。
 */
function splitTranslatableText(text: string): { body: string; trailing: string } {
  // 登場順を保ったまま収集する。正規表現は japanese-text.ts とほぼ同じだが独立。
  const patterns: RegExp[] = [
    /https?:\/\/\S+/gi,
    /(?:www\.)?[a-zA-Z0-9][-a-zA-Z0-9]*(?:\.[a-zA-Z0-9][-a-zA-Z0-9]*)*\.[a-zA-Z]{2,8}(?![a-zA-Z0-9])(?:\/[^\s]*)?/gi,
    /[#＃][\w\u3040-\u30ff\u4e00-\u9fff_ー\-]+/g,
    /@[A-Za-z0-9_.\-]+/g,
  ];
  const hits: { start: number; end: number; text: string }[] = [];
  for (const re of patterns) {
    for (const m of text.matchAll(re)) {
      if (m.index === undefined) continue;
      hits.push({ start: m.index, end: m.index + m[0].length, text: m[0] });
    }
  }
  if (hits.length === 0) return { body: text.trim(), trailing: '' };
  hits.sort((a, b) => a.start - b.start);
  // 重複 / 入れ子を除去
  const merged: { start: number; end: number; text: string }[] = [];
  for (const h of hits) {
    const last = merged[merged.length - 1];
    if (last && h.start < last.end) {
      if (h.end > last.end) last.end = h.end;
      continue;
    }
    merged.push({ ...h });
  }
  // body: merged の範囲をスペースで除去
  let body = '';
  let cursor = 0;
  for (const m of merged) {
    body += text.slice(cursor, m.start);
    body += ' ';
    cursor = m.end;
  }
  body += text.slice(cursor);
  body = body.replace(/\s+/g, ' ').trim();
  // trailing: 登場順に、スペース区切りで連結 (重複は残す: 元の投稿に合わせる)
  const trailing = merged.map((m) => text.slice(m.start, m.end)).join(' ').trim();
  return { body, trailing };
}

/** 訳文の本文部分が日本語比率を満たすか (URL/ハッシュタグ等の trailing は除外で判定済)。
 *  小モデルが時々原文をそのまま吐く / 英語ままを出すのを検知して retry に回すために使う。 */
function looksLikeJapaneseTranslation(cleanedBody: string): boolean {
  return hasJapanese(cleanedBody, 0.3);
}

/** runLLM が「日本語にならなかった」場合の専用エラー。retry で識別するため。
 *  attempted には英語のまま返ってきた翻訳結果を保持し、全 attempt が失敗した時に
 *  最後の出力として UI に渡せるようにする (空表示よりはマシ)。 */
class NonJapaneseTranslationError extends Error {
  attempted: string;
  constructor(attempted: string) {
    super('translation output is not Japanese enough');
    this.name = 'NonJapaneseTranslationError';
    this.attempted = attempted;
  }
}

const TRANSLATE_SYSTEM_PROMPT = 'あなたは SNS 投稿を日本語に翻訳する翻訳者です。';

/** sarashina: 段落ごとに専用プロンプトで訳して空行でつなぐ。 */
async function runSarashina(llm: LocalLLM, body: string, temperature: number): Promise<string> {
  const out: string[] = [];
  for (const para of splitParagraphs(body)) {
    const raw = await llm.generate(sarashinaTranslateInput(para), { temperature, maxNewTokens: sarashinaMaxNewTokens(para) });
    out.push(cleanSarashinaParagraph(raw));
  }
  return out.join('\n\n');
}

function withTimeout<T>(p: Promise<T>): Promise<T> {
  return Promise.race([
    p,
    new Promise<never>((_, rej) =>
      setTimeout(() => rej(new Error(`translate LLM timeout (${TIMEOUT_MS}ms)`)), TIMEOUT_MS),
    ),
  ]);
}

async function runLLM(
  llm: LocalLLM,
  text: string,
  langs: string[] | undefined,
  temperature: number,
): Promise<string> {
  const { body, trailing } = splitTranslatableText(text);
  if (!body) return trailing; // 本文なし → リンク等だけ返す
  if (llm.id === SARASHINA.id) {
    await loadSarashina(); // キャッシュだけから (再) 読み込み。止まれば sarashina-llm 側で打ち切る。60s の制限は生成だけに掛ける
    const cleaned = cleanTranslation(await withTimeout(runSarashina(llm, body, temperature)));
    const composed = trailing ? `${cleaned}\n${trailing}` : cleaned;
    if (!looksLikeJapaneseTranslation(cleaned)) throw new NonJapaneseTranslationError(composed);
    return composed;
  }
  const sourceHint = langs && langs.length > 0 ? `原文の言語: ${langs.join(', ')}。\n` : '';
  // LLM は入力末尾に最も注目するため、「指示を前に、原文を末尾に」の順で
  // 1 つの user メッセージに詰める。短文でも原文が埋もれにくい。
  const userPrompt = [
    'あなたはこれから SNS 投稿を日本語に翻訳します。次のルールを守ること:',
    '- 必ず日本語にする。原文の言語 (英語など) のまま出力してはいけない。',
    '- **口語・カジュアルな日本語** (「〜だよ」「〜なんだ」「〜じゃん」など) で、',
    '  友達に話しかけるような自然な調子にする。',
    '- 敬語・ですます調・硬い書き言葉・直訳調は避ける。',
    '- 出力は訳文のみの 1 段落。引用符・前置き・注釈は禁止。',
    sourceHint.trim() ? sourceHint.trim() : '',
    '',
    '以下が原文です。これを日本語に翻訳してください:',
    '---',
    body,
    '---',
  ].filter((l) => l !== '').join('\n');
  const result = await withTimeout(
    generateWithLocalLLM(
      { systemPrompt: TRANSLATE_SYSTEM_PROMPT, history: [{ role: 'user', content: userPrompt }] },
      { temperature, maxNewTokens: 300 },
      'translation',
    ),
  );
  if (!result) {
    // 利用可能な LLM が無い (Firefox/Safari/mobile 等)
    throw new Error('no local LLM available for translation');
  }
  const cleaned = cleanTranslation(result.text);
  const composed = trailing ? `${cleaned}\n${trailing}` : cleaned;
  if (!looksLikeJapaneseTranslation(cleaned)) {
    throw new NonJapaneseTranslationError(composed);
  }
  return composed;
}

/** 温度を段階的に上げて translate を再試行する。
 *  - auto (初回): [0, 0.7, 1.0] — 0 で安定、駄目なら確率的に揺らして突破
 *  - retry (ユーザ手動): [0.7, 1.0] — temp=0 cache と必ず違う出力にする
 *  全 attempt 失敗時は最後の英語出力を返す (空 throw より UX マシ)。
 *
 *  LLM 利用不可な環境では最初の attempt で即 throw (リトライ意味なし)。 */
async function runLLMWithRetry(
  text: string,
  langs: string[] | undefined,
  temperatures: readonly number[],
): Promise<string> {
  // LLM 利用不可なら即時 throw (retry chain 全部 fail 待ちは無駄)
  const llm = await pickLocalLLM(false, 'translation');
  if (!llm) throw new Error('no local LLM available for translation');
  let lastFail: NonJapaneseTranslationError | null = null;
  let lastError: unknown = null;
  for (let i = 0; i < temperatures.length; i++) {
    const temp = temperatures[i]!;
    try {
      return await runLLM(llm, text, langs, temp);
    } catch (e) {
      if (e instanceof SarashinaNotInstalledError) {
        // キャッシュが消えていた: ダウンロードせず未導入に戻す (翻訳 UI は消え、次の召喚の儀式で入れ直す)
        resetLocalLLMChoice();
        throw e;
      }
      if (e instanceof NonJapaneseTranslationError) {
        console.warn(`[translate] attempt ${i + 1}/${temperatures.length} not Japanese (temp=${temp})`);
        lastFail = e;
      } else {
        console.warn(`[translate] attempt ${i + 1}/${temperatures.length} threw`, e);
        lastError = e;
      }
    }
  }
  if (lastFail) return lastFail.attempted; // 英語ままでも最後の出力を返す
  throw lastError ?? new Error('translation failed (all attempts errored)');
}

const TRANSLATION_TEMPS_AUTO = [0, 0.7, 1.0] as const;
const TRANSLATION_TEMPS_RETRY = [0.7, 1.0] as const;

function cleanTranslation(raw: string): string {
  let t = stripMarkdown(raw).replace(/\r\n/g, '\n').trim();
  // LLM が「以下、翻訳です:」みたいな前置きを付けるケースを救済
  const preface = t.match(/^(翻訳|以下|訳文)[^\n]*?[:：]\s*/);
  if (preface) t = t.slice(preface[0].length);
  // 改行で分かれているが意味的に 1 段落なら連結。段落が複数なら保持。
  if (!/\n\s*\n/.test(t)) t = t.replace(/\n+/g, ' ');
  return stripWrappers(t);
}

export async function translateToJapanese(
  uri: string,
  text: string,
  langs?: string[] | undefined,
  opts: { force?: boolean } = {},
): Promise<string> {
  if (!opts.force) {
    const cached = await loadCachedTranslation(uri);
    // 旧バージョン (=temperature=0 単発で英語ままだった訳) のキャッシュが残ってる
    // ことがあるので、日本語比率で validate してから採用する。失敗ケースは miss
    // 扱いで再翻訳に進む。
    if (cached && hasJapanese(cached, 0.3)) return cached;
  }
  // force=true (ユーザが「再翻訳」ボタンを押した) は temp 0.7 から始めて、必ず
  // キャッシュと違う出力になるようにする。auto 経路は 0 → 0.7 → 1.0 の retry chain。
  const temps = opts.force ? TRANSLATION_TEMPS_RETRY : TRANSLATION_TEMPS_AUTO;
  const result = await enqueue(() => runLLMWithRetry(text, langs, temps));
  // 日本語として成立した翻訳のみキャッシュに保存。全 attempt 失敗で英語のまま
  // 帰ってきた場合はキャッシュしない (次回また試せる)。
  if (hasJapanese(result, 0.3)) {
    await saveCachedTranslation(uri, result);
  }
  return result;
}

// ── React フック ──
export type TranslationState = 'idle' | 'loading' | 'done' | 'error';

export interface UseTranslationResult {
  state: TranslationState;
  translated: string | undefined;
  error: string | undefined;
  /** 自動翻訳 OFF の時に手動で翻訳を開始するためのトリガ */
  triggerTranslate: () => void;
  /** キャッシュを無視して再翻訳 (プロンプト改善後のキャッシュ起因ズレの救済) */
  retranslate: () => void;
  /** 自動翻訳の対象か (= 非日本語と判定されたか)。UI でボタン表示可否に使う */
  isNonJapanese: boolean;
}

export function useTranslation(
  uri: string | undefined,
  text: string,
  langs?: string[] | undefined,
): UseTranslationResult {
  const [state, setState] = useState<TranslationState>('idle');
  const [translated, setTranslated] = useState<string | undefined>(undefined);
  const [error, setError] = useState<string | undefined>(undefined);
  const startedRef = useRef(false);

  // LLM 利用可否は async なので mount 後に確定する。null = 確認中、
  // false = 利用不可 (Firefox/Safari/モバイル等)、true = 使える。
  const [llmAvailable, setLlmAvailable] = useState<boolean | null>(null);
  useEffect(() => {
    let cancelled = false;
    pickLocalLLM(false, 'translation').then((llm) => {
      if (!cancelled) setLlmAvailable(!!llm);
    });
    return () => { cancelled = true; };
  }, []);
  const isNonJapanese = llmAvailable === true && shouldAutoTranslate(text, langs);
  const canTranslate = llmAvailable === true && Boolean(uri) && isNonJapanese;

  const run = useCallback((force: boolean) => {
    if (!uri || !isNonJapanese) return;
    if (!force && startedRef.current) return;
    startedRef.current = true;
    setState('loading');
    setError(undefined);
    (async () => {
      try {
        const out = await translateToJapanese(uri, text, langs, { force });
        setTranslated(out);
        setState('done');
      } catch (e) {
        console.warn('[translate] failed', e);
        // sarashina のキャッシュが消えて未導入に戻った等: LLM の無い端末と同じく翻訳 UI を消す
        if (!(await pickLocalLLM(false, 'translation'))) {
          setLlmAvailable(false);
          setState('idle');
          return;
        }
        setError(String((e as Error)?.message ?? e));
        setState('error');
      }
    })();
  }, [uri, text, langs, isNonJapanese]);

  const start = useCallback(() => run(false), [run]);
  const retranslate = useCallback(() => run(true), [run]);

  // 自動翻訳が有効なら即時開始
  useEffect(() => {
    if (!canTranslate) return;
    if (!getAutoTranslate()) return;
    start();
  }, [canTranslate, start]);

  // uri が変わった時は state をリセット (同じコンポーネントが別投稿に使い回される場合)
  useEffect(() => {
    startedRef.current = false;
    setState('idle');
    setTranslated(undefined);
    setError(undefined);
  }, [uri]);

  return { state, translated, error, triggerTranslate: start, retranslate, isNonJapanese: canTranslate };
}
